import React, { useState } from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY } from "../theme.js";
import { supabase, friendlyError } from "../lib/supabase.js";
import { LogoMark, Wordmark, Field, ErrorNote, Spinner } from "../components/ui.jsx";
import { inputStyle, primaryBtnStyle, secondaryBtnStyle, ghostBtnStyle, btnState } from "../components/styles.js";

const MODES = { SIGN_IN: "signin", SIGN_UP: "signup", RESET: "reset" };

export default function LoginScreen() {
  const [mode, setMode] = useState(MODES.SIGN_IN);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [busy, setBusy] = useState(null); // null | 'email' | 'google' | 'apple'
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const longEnough = password.length >= 6;
  const passwordsMatch = password === confirmPassword;
  // Only complain once they've started typing the confirmation — flagging a
  // mismatch against an empty field is just noise.
  const showMismatch = mode === MODES.SIGN_UP && confirmPassword.length > 0 && !passwordsMatch;

  const canSubmit =
    mode === MODES.RESET
      ? emailValid
      : mode === MODES.SIGN_IN
        ? emailValid && longEnough
        : emailValid && longEnough && passwordsMatch && confirmPassword.length > 0 && acceptedLegal;

  function switchMode(next) {
    setMode(next);
    setError(null);
    setNotice(null);
    setConfirmPassword("");
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy("email");
    setError(null);
    setNotice(null);

    try {
      if (mode === MODES.RESET) {
        const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: `${window.location.origin}/`,
        });
        if (err) throw err;
        setNotice("Password reset link sent — check your inbox.");
      } else if (mode === MODES.SIGN_UP) {
        const { data, error: err } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              display_name: displayName.trim() || email.trim().split("@")[0],
              accepted_terms: true,
              accepted_privacy: true,
              accepted_beta: true,
            },
          },
        });
        if (err) throw err;
        // With email confirmation enabled there is no session yet; say so
        // instead of leaving the user on a screen that looks like it failed.
        if (!data.session) {
          setNotice("Account created. Check your inbox to confirm your email, then sign in.");
          setMode(MODES.SIGN_IN);
          setPassword("");
        }
        // If confirmation is off, onAuthStateChange takes over from here.
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
        if (err) throw err;
      }
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleOAuth(provider) {
    if (busy) return;
    setBusy(provider);
    setError(null);
    setNotice(null);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (err) {
      setError(friendlyError(err));
      setBusy(null);
    }
    // On success the browser navigates away; no need to clear `busy`.
  }

  const heading =
    mode === MODES.SIGN_UP ? "Create Account" : mode === MODES.RESET ? "Reset your password" : "Welcome Back";
  const subheading =
    mode === MODES.SIGN_UP
      ? "Set up your Collector's Passport"
      : mode === MODES.RESET
        ? "We'll email you a reset link"
        : "Sign in to your account";

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        background: `linear-gradient(180deg, rgba(10,10,10,0.2), ${COLORS.black} 70%), linear-gradient(135deg, #1a1611, #0A0A0A)`,
        padding: "24px 24px 40px",
      }}
    >
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, minHeight: 180, paddingBottom: 18 }}>
        <LogoMark size={64} />
        <Wordmark size={22} />
      </div>

      <div style={{ textAlign: "center", marginBottom: 18 }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 18, color: COLORS.cream }}>{heading}</div>
        <div style={{ fontSize: 12.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 3 }}>{subheading}</div>
      </div>

      {error && (
        <div style={{ marginBottom: 12 }}>
          <ErrorNote message={error} />
        </div>
      )}
      {notice && (
        <div
          style={{
            marginBottom: 12,
            background: "rgba(76,175,109,0.10)",
            border: `1px solid ${COLORS.green}`,
            borderRadius: 12,
            padding: "12px 14px",
            fontSize: 12.5,
            fontFamily: FONT_BODY,
            color: COLORS.cream,
          }}
        >
          {notice}
        </div>
      )}

      <form onSubmit={handleEmailSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {mode === MODES.SIGN_UP && (
          <Field label="Display name">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How your Passport greets you"
              autoComplete="name"
              style={inputStyle}
            />
          </Field>
        )}

        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            style={inputStyle}
          />
        </Field>

        {mode !== MODES.RESET && (
          <Field label="Password">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete={mode === MODES.SIGN_UP ? "new-password" : "current-password"}
              style={inputStyle}
            />
          </Field>
        )}

        {mode === MODES.SIGN_UP && (
          <Field label="Confirm Password">
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Type it again"
              autoComplete="new-password"
              style={{ ...inputStyle, borderColor: showMismatch ? COLORS.red : COLORS.line }}
            />
            {showMismatch && (
              <span style={{ fontSize: 11.5, fontFamily: FONT_BODY, color: COLORS.red }}>
                Passwords don't match yet.
              </span>
            )}
            {mode === MODES.SIGN_UP && password.length > 0 && !longEnough && (
              <span style={{ fontSize: 11.5, fontFamily: FONT_BODY, color: COLORS.creamFaint }}>
                Password must be at least 6 characters.
              </span>
            )}
          </Field>
        )}

        {mode === MODES.SIGN_UP && (
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 11.5, fontFamily: FONT_BODY, color: COLORS.creamDim, lineHeight: 1.5, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={acceptedLegal}
              onChange={(e) => setAcceptedLegal(e.target.checked)}
              style={{ accentColor: COLORS.gold, marginTop: 2, flex: "none" }}
            />
            <span>
              I agree to the <b style={{ color: COLORS.goldBright }}>Beta Testing Agreement</b>, <b style={{ color: COLORS.goldBright }}>Terms</b> and{" "}
              <b style={{ color: COLORS.goldBright }}>Privacy Policy</b>.
            </span>
          </label>
        )}

        <button type="submit" disabled={!canSubmit || busy === "email"} style={{ ...btnState(primaryBtnStyle, canSubmit && busy !== "email"), display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {busy === "email" && <Spinner size={14} color="#1A1408" />}
          {mode === MODES.SIGN_UP ? "Create account" : mode === MODES.RESET ? "Send reset link" : "Sign in"}
        </button>
      </form>

      {mode !== MODES.RESET && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0 14px" }}>
            <span style={{ flex: 1, height: 1, background: COLORS.lineFaint }} />
            <span style={{ fontSize: 10.5, fontFamily: FONT_BODY, color: COLORS.creamFaint, letterSpacing: 1 }}>OR</span>
            <span style={{ flex: 1, height: 1, background: COLORS.lineFaint }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button type="button" onClick={() => handleOAuth("apple")} disabled={Boolean(busy)} style={{ ...btnState(secondaryBtnStyle, !busy), textAlign: "center" }}>
              🍎  Continue with Apple
            </button>
            <button type="button" onClick={() => handleOAuth("google")} disabled={Boolean(busy)} style={{ ...btnState(secondaryBtnStyle, !busy), textAlign: "center" }}>
              G  Continue with Google
            </button>
          </div>
        </>
      )}

      <div style={{ textAlign: "center", marginTop: 20, fontSize: 12.5, fontFamily: FONT_BODY, color: COLORS.creamFaint }}>
        {mode === MODES.SIGN_IN && (
          <>
            Don't have an account?{" "}
            <span style={{ color: COLORS.goldBright, cursor: "pointer" }} onClick={() => switchMode(MODES.SIGN_UP)}>
              Sign Up
            </span>
            <div style={{ marginTop: 10 }}>
              <button type="button" onClick={() => switchMode(MODES.RESET)} style={{ ...ghostBtnStyle, fontSize: 12 }}>
                Forgot your password?
              </button>
            </div>
          </>
        )}
        {mode === MODES.SIGN_UP && (
          <>
            Already have a Passport?{" "}
            <span style={{ color: COLORS.goldBright, cursor: "pointer" }} onClick={() => switchMode(MODES.SIGN_IN)}>
              Sign In
            </span>
          </>
        )}
        {mode === MODES.RESET && (
          <button type="button" onClick={() => switchMode(MODES.SIGN_IN)} style={{ ...ghostBtnStyle, fontSize: 12.5, color: COLORS.goldBright }}>
            ← Back to sign in
          </button>
        )}
      </div>

      <div style={{ textAlign: "center", marginTop: 22, fontSize: 10, fontFamily: FONT_BODY, color: COLORS.creamFaint, lineHeight: 1.6 }}>
        SENOGARA is an independent collection-management tool and is not affiliated with,
        endorsed by, or sponsored by any brand catalogued within it.
      </div>
    </div>
  );
}
